import { AuthApiS, AuthConstantS, AuthErrorS, AuthTypeS, AuthValidationS } from '@/features/Auth/outbound/share';
import { LOGIN_IMAGE } from '@/libs/constant';
import { APP_STORE_LINK, VALID_APP_VERSION } from '@/libs/constant/AppConstant';
import { isFeatureError } from '@/libs/errors/FeatureError';
import { AT, T } from '@/libs/translation';
import { compareAppVersion } from '@/libs/util/compareAppVersion';
import { makeResultUrl } from '@/libs/util/makeServiceUrl';
import { initBackForward, initChangeWindow, isBackForward } from '@/libs/util/windowEventHelper';
import { Button, ButtonSnsIcon, Icon, Input, InputChangeEvent, Link, Typo } from '@/ui/component/atom';
import { Header, Layout, Modal } from '@/ui/component/cell';
import { AutoCompleteEmailView, FindIdView, makeErrorPopup, makePopup, ResetPasswordView } from '@/ui/component/view';
import { debounce, getSharedData } from '@/ui/util';
import { fromEntries, map, pipe, toArray, values } from '@fxts/core';
import { isAxiosError } from 'axios';
import { html, type Html, Page, View } from 'rune-ts';
import { PostMessage } from '../../../util/postMessage';
import { LogoView } from '../../view/Logo/Logo';
import auth_klass from '../Common/Auth.module.scss';
import klass from './LoginPage.module.scss';

export interface LoginPageData {
  current_login_type?: AuthTypeS.USER_LOGIN_TYPE;
  email?: string;
}

export class LoginPageBodyView extends View<LoginPageData> {
  component: {
    email: Input;
    password: Input;
    submit: Button;
  };

  constructor(data: LoginPageData) {
    super(data);

    this.component = {
      email: new Input(
        { value: data.email ?? '' },
        {
          attr: { name: 'email', type: 'email', placeholder: T('auth.login.email_placeholder') },
        },
      ),
      password: new Input(
        {},
        { attr: { name: 'password', type: 'password', placeholder: T('auth.login.password_placeholder') } },
      ),
      submit: new Button(
        { label: new Typo({ text: T('auth.login.submit') }, { weight: 'bold' }) },
        { size: 'large', disabled: true, type: 'submit' },
      ),
    };
  }

  override template() {
    const { theme, client_id, device, lang } = getSharedData(this);

    return html`<div class="${auth_klass.page}">
      <div class="${auth_klass.container}">
        ${new LogoView({ theme })}
        <div class="${auth_klass.section}">
          <form class="${klass.signin} signin">
            <div class="${klass.input_container}">
              ${new AutoCompleteEmailView({}, this.component.email)}${this.component.password}
            </div>
            <div class="${klass.button_container}">
              ${this.component.submit}
              <div class="${klass.modal_button_container}">
                ${lang === 'kr'
                  ? html`
                      ${new Typo(
                        { text: T('auth.find_id.title') },
                        { typo: 'p2', klass: `${auth_klass.button} find_id` },
                      )}
                      <span class="${klass.divider}"></span>
                    `
                  : ''}
                ${new Typo(
                  {
                    text: html`${T('auth.reset_password.title')}
                    ${theme === 'Ciety'
                      ? html`
                          <div class="${klass.ciety_password}">
                            ${new Icon({ name: 'pin', klass: klass.pin })}
                            <div class="${klass.text}">
                              ${new Typo({ text: T('auth.login.ciety_password') }, { typo: 'p3' })}
                            </div>
                          </div>
                        `
                      : ''}`,
                  },
                  { typo: 'p2', klass: `${auth_klass.button} reset_password` },
                )}
              </div>
            </div>
          </form>
          <section class="${klass.sns_signin}">
            ${new Typo({ text: T('auth.login.sns_signin') }, { typo: 'p1', color: 'gray_80' })}
            <div class="${klass.button_container}">
              ${pipe(
                AuthConstantS.USER_LOGIN_SNS_TYPE_MAPPER,
                values,
                map((type) => {
                  return html`
                    <div class="${klass.button_wrapper}">
                      ${new Link(
                        {
                          label: new ButtonSnsIcon(
                            {
                              type,
                              label:
                                type === 'KAKAO'
                                  ? new Typo({ text: T('auth.login.kakao', { context: device }) })
                                  : undefined,
                            },
                            { active: true, button_type: type === 'KAKAO' ? 'label' : 'mini' },
                          ),
                          href: `/oauth/request/${type.toLowerCase()}?client_id=${client_id}`,
                        },
                        { klass: 'sns_login_link', title: type },
                      )}
                      ${this.data.current_login_type === type
                        ? html`
                            <div class="${klass.current_sns}">
                              ${new Icon({ name: 'pin' })}
                              <div class="${klass.text}">
                                ${new Typo({ text: T('auth.login.current_sns') }, { typo: 'p3' })}
                              </div>
                            </div>
                          `
                        : ''}
                    </div>
                  `;
                }),
                toArray,
              )}
            </div>
          </section>
          <section class="${klass.signup}">
            ${new Typo({ text: T('auth.login.signup_request') }, { typo: 'p1', color: 'gray_80' })}
            ${new Link({
              label: new Typo({ text: T('auth.login.go_signup') }, { typo: 'p1' }),
              href: `/${AT.language}/signup?client_id=${client_id}`,
            })}
          </section>
        </div>
      </div>
      ${device === 'mo'
        ? ''
        : html`<img class="${klass.theme_image}" src="${LOGIN_IMAGE[theme]}" alt="${theme}_login_image" />`}
    </div>`;
  }

  override onRender() {
    const { lang, app_info, theme } = getSharedData(this);

    const changeHandler = debounce(this.formChangeHandler.bind(this), 100);
    this.delegate(InputChangeEvent, Input, changeHandler);
    this.delegate('input', '.signin', changeHandler);
    this.delegate('change', '.signin', changeHandler);
    this.delegate('submit', '.signin', this.formSubmitHandler);
    this.delegate('click', '.reset_password', this.resetPasswordHandler);
    this.delegate('click', '.sns_login_link', async (e) => {
      if (app_info && compareAppVersion(app_info.version, VALID_APP_VERSION[theme]) === -1) {
        e.originalEvent.preventDefault();

        const res = await makePopup({
          title: T('app.need_update.title'),
          message: T('app.need_update.message'),
          ok: T('app.need_update.ok'),
          cancel: T('common.cancel'),
        });

        if (res === 'confirm') {
          location.href = APP_STORE_LINK[theme][app_info.os];
        }
      }
    });

    if (lang === 'kr') {
      this.delegate('click', '.find_id', this.findIdHandler);
    }

    this.component.email.focus();

    this.browserStatusChangeHandler();
  }

  browserStatusChangeHandler() {
    if (isBackForward()) this.formChangeHandler();
    initBackForward(this.formChangeHandler.bind(this));
    initChangeWindow(this.formChangeHandler.bind(this));
  }

  formChangeHandler() {
    if (this.component.email.value && this.component.password.value && this.component.submit.option.disabled) {
      this.component.submit.enabled();
      return;
    }

    if ((!this.component.email.value || !this.component.password.value) && !this.component.submit.option.disabled) {
      this.component.submit.disabled();
    }
  }
  async formSubmitHandler(e: Event & { originalEvent: Event }) {
    e.originalEvent.preventDefault();
    const { client_id } = getSharedData(this);
    const form = e.currentTarget as HTMLFormElement;

    try {
      await pipe(new FormData(form).entries(), fromEntries, AuthValidationS.validateSigninReqBody, AuthApiS.postSignin);

      return location.replace(makeResultUrl(client_id));
    } catch (e) {
      if (isFeatureError(e)) {
        return makeErrorPopup(e, e.json);
      }

      if (isAxiosError(e)) {
        if (e.response?.data?.code === AuthErrorS.USER_003.name) {
          const res = await makeErrorPopup(e, {
            ...e.response?.data,
            cancel: T('common.cancel'),
            ok: T('auth.login.signup'),
          });
          if (res === 'confirm') {
            location.href = `/${AT.language}/signup?client_id=${client_id}`;
          }
          return;
        } else if (e.response?.data?.code === AuthErrorS.USER_004_01.name) {
          const res = await makeErrorPopup(e, {
            ...e.response?.data,
            ok: T('auth.login.password_register'),
            cancel: T('common.cancel'),
          });
          if (res === 'confirm') {
            return this.resetPasswordHandler();
          } else {
            return;
          }
        } else if (e.response?.data?.code === AuthErrorS.USER_004.name) {
          return makeErrorPopup(e, {
            title: T('auth.login.no_password'),
            message: T('auth.login.no_password_message'),
          });
        }
        return makeErrorPopup(e, e.response?.data);
      }

      return makeErrorPopup(AuthErrorS.SIGNIN_001().json);
    }
  }
  resetPasswordHandler() {
    return Modal.open(
      { view: new ResetPasswordView({}) },
      {
        header: { title: T('auth.reset_password.title') },
        footer: {
          cancel: T('common.cancel'),
          confirm: T('auth.reset_password.reset'),
          confirm_option: {
            disabled: true,
          },
        },
        backdrop: true,
      },
    );
  }
  async findIdHandler() {
    await Modal.open(
      { view: new FindIdView({}) },
      {
        header: { title: T('auth.find_id.title') },
        footer: {
          cancel: T('common.cancel'),
          confirm: T('auth.find_id.title', { context: 'mini' }),
          confirm_option: {
            disabled: true,
          },
        },
        backdrop: true,
      },
    );
  }
}

export class LoginPage extends Page<LoginPageData> {
  protected override template(): Html {
    const { device } = getSharedData(this);
    return html`
      <div>
        ${new Layout(
          { view: new LoginPageBodyView(this.data) },
          {
            fullwidth: true,
            header: device === 'mo' ? new Header({}) : undefined,
          },
        )}
      </div>
    `;
  }

  override onRender() {
    this.delegate(Header.HeaderBackEvent, Header, () => {
      const { app_info } = getSharedData(this);

      if (app_info?.name === 'MarppleShop') {
        PostMessage.goBack();
      } else {
        history.back();
      }
    });
  }
}
